import React from 'react';
import { withCookies, Cookies } from 'react-cookie/es6';

import { Button, Select } from 'plaid-threads';
import { DropdownItem } from 'plaid-threads/Dropdown';

import './index.scss';
import { AppContext } from '../App';
import Flag from './Flag';
import { CountryCode, CountryCodeDisplayNames, Language } from '../constants';
import PlaidLogo from '../assets/plaid-logo.svg';
import M2020Logo from '../assets/m2020logo.png';
import { isMobile } from 'react-device-detect';
import { useHistory } from 'react-router';
import axios from 'axios';
import { SelectGroupOptionsType } from 'plaid-threads/SelectGroup';

interface CountryCodeOption extends DropdownItem {
  label: string;
  value: CountryCode;
}

const countryCodeOptions: Array<CountryCodeOption> = Object.entries(
  CountryCodeDisplayNames,
)
  .map(([key, value]) => ({
    label: value,
    value: key as CountryCode,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const languageOptions: Array<{ label: string; value: Language }> = [
  {
    label: 'Danish',
    value: 'da',
  },
  {
    label: 'Dutch',
    value: 'nl',
  },
  {
    label: 'English',
    value: 'en',
  },
  {
    label: 'French',
    value: 'fr',
  },
  {
    label: 'German',
    value: 'de',
  },
  {
    label: 'Italian',
    value: 'it',
  },
  {
    label: 'Italian',
    value: 'it',
  },
  {
    label: 'Latvian',
    value: 'lv',
  },
  {
    label: 'Lituanian',
    value: 'lt',
  },
  {
    label: 'Norwegian',
    value: 'no',
  },
  {
    label: 'Polish',
    value: 'pl',
  },
  {
    label: 'Portuguese',
    value: 'pt',
  },
  {
    label: 'Romanian',
    value: 'ro',
  },
  {
    label: 'Spanish',
    value: 'es',
  },
  {
    label: 'Swedish',
    value: 'sv',
  },
];

export const MultiCountryCodeSelector: React.FC = () => {
  const { dispatch, countryCode } = React.useContext(AppContext);
  return (
    <>
      <Select
        id="multiCountrySelect"
        errorMessage={
          countryCode.length === 0 ? 'At least one market is required' : ''
        }
        isMulti
        isClearable
        label="Markets"
        value={countryCodeOptions.filter(
          (o) => countryCode.indexOf(o.value) >= 0,
        )}
        onChange={(selected: SelectGroupOptionsType[]) => {
          dispatch({
            type: 'SET_COUNTRY_CODE',
            payload: {
              countryCode: selected.map((option) => option.value),
            },
          });
        }}
        options={countryCodeOptions}
      ></Select>
      {countryCode.map((country) => (
        <Flag key={'flag_' + country} countryCode={country} />
      ))}
    </>
  );
};

export const SingleCountryCodeSelector: React.FC = () => {
  const { dispatch, countryCode } = React.useContext(AppContext);
  return (
    <>
      <Select
        id="multiCountrySelect"
        errorMessage={
          countryCode.length === 0 ? 'At least one market is required' : ''
        }
        label="Markets"
        value={countryCodeOptions.filter(
          (o) => countryCode.indexOf(o.value) >= 0,
        )}
        onChange={(selected: SelectGroupOptionsType) => {
          dispatch({
            type: 'SET_COUNTRY_CODE',
            payload: {
              countryCode: [selected.value],
            },
          });
        }}
        options={countryCodeOptions}
      ></Select>
      {countryCode.map((country) => (
        <Flag key={'flag_' + country} countryCode={country} />
      ))}
    </>
  );
};

export const LanguageSelector: React.FC = () => {
  const { dispatch, language } = React.useContext(AppContext);
  return (
    <>
      <Select
        id="multiCountrySelect"
        label="Language"
        value={languageOptions.find((o) => language === o.value)}
        onChange={(selected: SelectGroupOptionsType) => {
          dispatch({
            type: 'SET_LANGUAGE',
            payload: {
              language: selected.value,
            },
          });
        }}
        options={languageOptions}
      ></Select>
    </>
  );
};

interface LogoutProps {
  cookies: Cookies;
}

const LogoutButton: React.FC<LogoutProps> = (props) => {
  const { dispatch } = React.useContext(AppContext);
  const history = useHistory();

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const key = params.get('key');
  const timestamp = params.get('timestamp');
  const urlQueryParams =
    '?' +
    (key ? 'key=' + key : '') +
    (timestamp ? '&timestamp=' + timestamp : '');

  return (
    <div className="Logout">
      <Button
        secondary
        small={isMobile}
        inline={isMobile}
        onClick={async () => {
          await axios.post(
            `${process.env.REACT_APP_SERVER_URL}/token/logout`,
            {},
            { withCredentials: true },
          );
          dispatch({
            type: 'SET_SUCCESSFUL_EXTRACTION',
            payload: {
              successfulExtraction: false,
            },
          });
          dispatch({
            type: 'SET_PAGE',
            payload: {
              page: 'landing',
            },
          });
          // This is necessary for the redirect flow. Upon logout
          // the user should be rerouted to /demo without any query parameters
          history.push({
            pathname: '/demo',
            search: urlQueryParams,
          });
        }}
      >
        Log out
      </Button>
    </div>
  );
};

export const ReturnToHomeButton: React.FC = () => {
  const { dispatch } = React.useContext(AppContext);
  const history = useHistory();

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const key = params.get('key');
  const timestamp = params.get('timestamp');
  const urlQueryParams =
    '?' +
    (key ? 'key=' + key : '') +
    (timestamp ? '&timestamp=' + timestamp : '');

  return (
    <Button
      onClick={async () => {
        await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/token/logout`,
          {},
          { withCredentials: true },
        );
        dispatch({
          type: 'SET_SUCCESSFUL_EXTRACTION',
          payload: {
            successfulExtraction: false,
          },
        });
        dispatch({
          type: 'SET_PAGE',
          payload: {
            page: 'landingV2',
          },
        });
        // This is necessary for the redirect flow. Upon logout
        // the user should be rerouted to /demo without any query parameters
        history.push({
          pathname: '/home',
          search: urlQueryParams,
        });
      }}
    >
      Return to Home Page
    </Button>
  );
};

interface Props {
  cookies: Cookies;
  isM2020?: boolean;
}

const Header: React.FC<Props> = ({ cookies, isM2020 }: Props) => {
  const { successfulExtraction, page } = React.useContext(AppContext);
  // Logout button is only applicable for old AIS and PIS result pages (NOT aisV2 & pisV2)
  const showLogout = successfulExtraction && (page === 'ais' || page === 'pis');
  return (
    <div className="Header">
      <div className="Logo">
        <a href={window.location.href}>
          <img
            src={PlaidLogo}
            alt="Plaid"
            height={isMobile ? '24px' : '50px'}
          />
        </a>
      </div>
      {isM2020 && (
        <div className="Logo__M2020">
          <img
            src={M2020Logo}
            alt="M2020 Europe"
            height={isMobile ? '24px' : '50px'}
          />
        </div>
      )}
      <div className="Components">
        {showLogout && <LogoutButton cookies={cookies} />}
      </div>
    </div>
  );
};

export default withCookies(Header);
